import React, { useState, useEffect } from 'react';
import Host from './Host/Host';
import queryString from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSpaceActivities,
  getCancellationPolicies,
  getPricingOptions,
  getAmenities,
  getRules,
  getAddons,
  submitSpaceDetails,
  submitSpaceDetailsMeet,
  submitSpaceDetailsTrain,
  getSeatingArrangement,
  getSavings,
  fetchSpaceDetails,
  getApplicableTaxes,
  updateSpaceDetails,
  fetchPlatformData,
  getSpaceCategoryDetails,
  getPackageTypes,
  getPricingOptionsDayArray,
} from '../SpaceAction';
import { Form, Input, Button, Checkbox, Spin } from 'antd';
import BasicDetails from './BasicDetails/BasicDetails';
import HotDesk from './HotDesk/HotDesk';
import DedicatedDesk from './DedicatedDesk/DedicatedDesk';
import PrivateOfficeOrBooth from './PrivateOfficeOrBooth/PrivateOfficeOrBooth';
import MeetAndTrain from './MeetAndTrain/MeetAndTrain';
import toastr from 'toastr';
import Payout from './Payout/Payout';
import { setValuesToFields } from './../../../../helper/editSpaceDataSet';
import { BOOKING_TYPE, SPACE_ACTIVITIES } from '../../../common/constants';
import { setDescriptionError } from '../../../../redux/actions/uistate';
import { resetSpaceFormData } from '../SpaceAction';

const layout = {
  labelCol: {
    span: 6,
  },
  wrapperCol: {
    span: 16,
  },
};

const tailLayout = {
  wrapperCol: {
    offset: 8,
    span: 16,
  },
};

const SpaceEdit = (props) => {
  const dispatch = useDispatch();
  const { spcId, spcCategory } = queryString.parse(props.location.search);
  let isEdit = false;
  if (spcId && (spcCategory === 'Work' || spcCategory === 'Meet' || spcCategory === 'Shoot'))
    isEdit = true;

  const spaceDetails = useSelector((state) => state.space?.spaceDetails?.data || []);
  const [fetchedData, setFetchedData] = useState(false);
  const [initialState, setInitialState] = useState({});
  const [showDescriptionError, setShowDescriptionError] = useState(false);
  const [category, setCategory] = useState(
    spaceDetails?.events ? spaceDetails.events[0].eventType.spaceCategory.name : null
  );
  const [workActivityId, setWorkActivityId] = useState();
  const [meetTrainActivityIds, setMeetTrainActivityIds] = useState([]);
  // const spaceActivities = useSelector(state => state.space?.spaceActivities?.data || []);

  const spaceActivities = useSelector((state) => state.space?.spaceCategory?.data || []);
  const [venueTypes, setVenueTypes] = useState([]);
  const [currency, setCurrency] = useState(null);
  const [form] = Form.useForm();
  const [spaceImages, setSpaceImages] = useState(spaceDetails?.images || []);
  const rules = useSelector((state) => state.space?.rules?.data?.rules || []);
  const [countryCodeSelection, setCountryCodeSelection] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const descriptionError = useSelector((state) => state.uiState.descriptionError);

  const [initialFormValues, setInitialFormValues] = useState(null);
  const [initialImages, setInitialImages] = useState([]);
  const [formHasChanged, setFormHasChanged] = useState(false);
  const [initialDataSet, setInitialDataSet] = useState(false);
  const [originalFormSnapshot, setOriginalFormSnapshot] = useState(null);

  useEffect(() => {
    // Reset form data when creating a new space
    if (!isEdit) {
      dispatch(resetSpaceFormData());
      form.resetFields();
      setSpaceImages([]);
      setCategory(null);
      setWorkActivityId(null);
      setMeetTrainActivityIds([]);
      setFormHasChanged(true); // Allow submission for new spaces
    }
  }, [isEdit, dispatch, form]);

  useEffect(() => {
    resetFormData();
    dispatch(getSpaceActivities());
    dispatch(getCancellationPolicies());
    dispatch(getPricingOptions());
    dispatch(getAmenities());
    dispatch(getAddons());
    dispatch(getRules());
    dispatch(getSeatingArrangement());
    dispatch(getSavings());
    dispatch(fetchPlatformData());
  }, []);

  useEffect(() => {
  if (!spcId) {
    dispatch(resetSpaceFormData());
  }
}, [spcId, dispatch]);

  useEffect(() => {
    if (isEdit) {
      dispatch(getSpaceCategoryDetails(spaceDetails?.location?.country));
      dispatch(getAmenities(spaceDetails?.location?.country));
      dispatch(getRules(spaceDetails?.location?.country));
      dispatch(getPackageTypes(spaceDetails?.location?.country));
      dispatch(
        getPricingOptionsDayArray(
          spaceDetails?.events?.[0]?.chargings?.map((type) => type.chargingType.id)
        )
      );
    }
  }, [fetchedData]);

  useEffect(() => {
    if (isEdit) {
      dispatch(fetchSpaceDetails(spcId, spcCategory, setFetchedData));
      setCategory(spcCategory);
    }
  }, []);

  useEffect(() => {
    if (isEdit) {
      setValuesToFields(
        spaceDetails,
        form,
        setWorkActivityId,
        setCurrency,
        dispatchTax,
        spcCategory
      );
      setSpaceImages(
        spaceDetails?.images && spaceDetails.images.length > 0
          ? spaceDetails?.images.map((img) => {
              return {
                url: img.url,
                publicId: img.publicId,
                etag: img.etag,
                signature: img.signature,
                isPrimary: img.isPrimary,
                name: img.name,
              };
            })
          : []
      );
      if (spcCategory !== 'Work')
        setMeetTrainActivityIds(
          spaceDetails.eventTypes ? spaceDetails.eventTypes.map((evnt) => evnt.id) : null
        );
    }
  }, [fetchedData]);

  useEffect(() => {
    if (isEdit) {
      if (fetchedData && !initialDataSet) {
        setTimeout(() => {
          const completeFormValues = form.getFieldsValue(true);
          setInitialFormValues(completeFormValues);
          setOriginalFormSnapshot(JSON.stringify(completeFormValues));
          setInitialImages(
            spaceDetails?.images && spaceDetails.images.length > 0
              ? [...spaceDetails.images.map((img) => ({
                  url: img.url,
                  publicId: img.publicId,
                  etag: img.etag,
                  signature: img.signature,
                  isPrimary: img.isPrimary,
                }))]
              : []
          );
          setInitialDataSet(true);
          setFormHasChanged(false);
        }, 300);
      }
    }
  }, [fetchedData]);

  const getEventIds = () => {
    return initialState.events.map((i) => i.evenType.id);
  };

  const resetFormData = () => {
    if (!isEdit) {
      setCategory(null);
      form.resetFields();
      setSpaceImages([]);
      setWorkActivityId(null);
      setMeetTrainActivityIds([]);
      setFormHasChanged(true); // Ensure new spaces can be submitted
    } else {
      // Keep existing logic for edit mode
      setCategory([]);
      form.resetFields();
      setSpaceImages(Object.values(initialState).length ? initialState.images : []);
    }
  };

  const countryFiltering = (val) => {
    setCountryCodeSelection(val);
  };

  useEffect(() => {
    if (Boolean(spaceActivities.length) && Object.values(initialState).length) {
      const { venueType, name } = spaceActivities?.find(
        (i) => i.id === initialState.events[0].evenType.spaceActivities.id
      );
      setCategory(name);
      setVenueTypes(venueType);
      if (name === 'Work') {
        setWorkActivityId(getEventIds()[0]);
        form.setFieldsValue({ eventId: getEventIds()[0] });
      } else {
        setMeetTrainActivityIds(getEventIds()[0]);
        form.setFieldsValue({ eventId: getEventIds()[0] });
      }
      setSpaceImages(Object.values(initialState).length ? initialState.images : []);
    }
  }, [spaceActivities]);

  useEffect(() => {
    if (Boolean(spaceActivities.length) && isEdit && workActivityId) {
      const { venueType } = spaceActivities?.find((i) => i.name === spcCategory);
      setVenueTypes(venueType);
    }
  }, [spaceActivities, workActivityId]);

  const dispatchTax = (country) => {
    dispatch(getApplicableTaxes(country));
  };

  const getMeetOrTrainPricingOptions = (result) => {
    return Object.keys(result)
      .map((value, index) =>
        result[value].map((raw) => {
          return {
            name: value,
            startTime: raw.startTime,
            endTime: raw.endTime,
            price: { amount: raw.pricePerHour, currency: currency, currencySymbol: currency },
          };
        })
      )
      .map((i) => i)
      .reduce((accumulator, currentValue) => accumulator.concat(currentValue), []);
  };

  const handleImageChange = (images) => {
    setSpaceImages(images);

    if (isEdit && initialImages && initialDataSet) {
      const imagesHaveChanged =
        images.length !== initialImages.length ||
        images.some((img, idx) =>
          !initialImages[idx] || img.url !== initialImages[idx].url
        );

      if (imagesHaveChanged) {
        setFormHasChanged(true);
      }
    }
  };

  const checkFormChanged = () => {

    if (!isEdit) return true; // Always return true for new spaces

    if (!initialDataSet || !originalFormSnapshot) return false;

    try {
      const currentFormSnapshot = JSON.stringify(form.getFieldsValue(true));
      const hasChanges = currentFormSnapshot !== originalFormSnapshot;

      let imagesChanged = false;
      if (spaceImages && initialImages) {
        if (spaceImages.length !== initialImages.length) {
          imagesChanged = true;
        } else {
          for (let i = 0; i < spaceImages.length; i++) {
            if (spaceImages[i].url !== initialImages[i].url) {
              imagesChanged = true;
              break;
            }
          }
        }
      }

      if (formHasChanged !== (hasChanges || imagesChanged)) {
        setFormHasChanged(hasChanges || imagesChanged);
      }

      return hasChanges || imagesChanged;
    } catch (error) {
      console.error("Error checking form changes:", error);
      return false;
    }
  };

  const onFieldsChange = (changedFields, allFields) => {
    // Clear any lingering descriptionError on field change
    if (descriptionError) {
      dispatch(setDescriptionError(false));
    }

    // For new spaces, always mark form as changed
    if (!isEdit) {
      setFormHasChanged(true);
      return;
    }
    
    if (isEdit && initialDataSet && originalFormSnapshot) {
      setTimeout(() => {
        const currentSnapshot = JSON.stringify(form.getFieldsValue(true));
        const hasChanged = currentSnapshot !== originalFormSnapshot;

        if (formHasChanged !== hasChanged) {
          setFormHasChanged(hasChanged);
        }
      }, 0);
    }
  };

  const onFinish = (values) => {
    setIsSaving(true);

    var keepInMindTest = rules.map((k) => {
      return { rule: k.id, isDisabled: false };
    });
    const seatingArr = {
      meet: false,
      available: false,
    };
    values.images = spaceImages;
    if (category === 'Work') {
      values.events.map((event) => {
        if (event.bufferPeriod) event.bufferPeriod = { amount: event.bufferPeriod, unit: 'hour' };
        event.amenities =
          (event?.amenities || []).map((i) => {
            return { id: i };
          }) || [];
        event.addOns = (event?.addOns || [])?.filter((i) => i && i.isAvailable);
        event.keepInMind = event?.keepInMind
          .filter((i) => i && i.isDisabled)
          .map((k) => {
            return { rule: k.rule.id, isDisabled: !k.isDisabled };
          });
        event.keepInMind.map((k) => {
          keepInMindTest.map((i) => {
            if (i.rule === k.rule) {
              i.isDisabled = true;
            }
          });
        });
        event.keepInMind = keepInMindTest;
        event.seatArrangements = event?.seatArrangements.map((i) => {
          return { min: 1, max: i.max };
        });
        if (getVenueName(workActivityId) === SPACE_ACTIVITIES.HOT_DESK) {
          event.chargings
            .filter((i) => i.access24by7 === true && i.chargingTypeDesc.name === 'Daily')
            .map((k) => {
              return Object.assign(k, {
                pricingOptions: [
                  {
                    name: 'Daily',
                    price: {
                      amount: k.amount,
                      currency: values.spaceCurrency,
                      currencySymbol: values.spaceCurrency,
                    },
                  },
                ],
              });
            });
          event.chargings
            .filter((i) => i.access24by7 === true && i.chargingTypeDesc.name === 'Weekly')
            .map((k) => {
              return Object.assign(k, {
                pricingOptions: [
                  {
                    name: 'Daily',
                    price: {
                      amount: k.amount,
                      currency: values.spaceCurrency,
                      currencySymbol: values.spaceCurrency,
                    },
                  },
                ],
              });
            });
          event.chargings
            .filter((i) => i.access24by7 === true && i.chargingTypeDesc.name === 'Monthly')
            .map((k) => {
              return Object.assign(k, {
                pricingOptions: [
                  {
                    name: 'Daily',
                    price: {
                      amount: k.amount,
                      currency: values.spaceCurrency,
                      currencySymbol: values.spaceCurrency,
                    },
                  },
                ],
              });
            });
        }else if (getVenueName(workActivityId) === 'WORK_VIR') {
          // Virtual Office - only monthly charging
          event.chargings
            .filter((i) => i.access24by7 === true && i.chargingTypeDesc.name === 'Monthly')
            .map((k) => {
              return Object.assign(k, {
                pricingOptions: [
                  {
                    name: 'Daily',
                    price: {
                      amount: k.amount,
                      currency: values.spaceCurrency,
                      currencySymbol: values.spaceCurrency,
                    },
                  },
                ],
              });
            });
        }  
        else {
          event.chargings
            .filter((i) => i.access24by7 === true)
            .map((k) => {
              return Object.assign(k, {
                pricingOptions: [
                  {
                    name: 'Daily',
                    price: {
                      amount: k.amount,
                      currency: values.spaceCurrency,
                      currencySymbol: values.spaceCurrency,
                    },
                  },
                ],
              });
            });
        }
        event.spaceAddOns = (event?.addOns || [])?.map((i) => {
          return {
            addOn: {
              id: i.id,
            },
            charge: {
              price: {
                amount: i.charge.price,
                currency: values.spaceCurrency,
                currencySymbol: values.spaceCurrency,
              },
            },
            description: i.description,
          };
        });
        event.chargings.map((nPeriod) => (nPeriod.noticePeriod = values.noticePeriod));
        event.chargings.map((i) => delete i.amount);
        delete event.addOns;
        delete event.maximumSeats;
        return event;
      });
    } else if (category === 'Meet') {
      values.keepInMindMeet = values?.keepInMindMeet
        .filter((i) => i && i.isDisabled)
        .map((k) => {
          if (k?.rules) return { rule: k.rules.id, isDisabled: !k.isDisabled };
          else return { rule: k.rule.id, isDisabled: !k.isDisabled };
        });
      values.keepInMindMeet.map((k) => {
        keepInMindTest.map((i) => {
          if (i.rule === k.rule) {
            i.isDisabled = true;
          }
        });
      });
      values.keepInMindMeet = keepInMindTest;
      values.keepInMind = values.keepInMindMeet;
      delete values.keepInMindMeet;

      seatingArr.meet = true;
      if (values.seatArrangements) {
        seatingArr.available = true;
      }
      values.seatArrangements
        ? (values.seatArrangements = values?.seatArrangements
            .filter((k) => k)
            .map((i) => Object.assign(i, { max: parseInt(values.maximumSeats) })))
        : toastr.error("Seating arrangement can't be null.");
      values.chargings.map((k) => {
        return Object.assign(k, { pricingOptions: getMeetOrTrainPricingOptions(k.pricingOptions) });
      });
      values.chargings.map((k) => {
        k.pricingOptions = k.pricingOptions.filter((pricingOpt) => {
          if (pricingOpt) return pricingOpt;
        });
        if (k.discounts?.length > 0) {
          k.discounts = k.discounts.filter((dsc) => {
            if (dsc.id) return dsc;
          });
          if (k.discounts.length < 1) delete k.discounts;
        }
      });
      if (meetTrainActivityIds?.length > 0)
        values.eventTypes = meetTrainActivityIds.map((mt) => {
          return { id: mt };
        });
      values.amenities =
        (values?.amenities || []).map((i) => {
          return { id: i };
        }) || [];
      values.addOns = (values?.addOns || [])?.filter((i) => i && i.isAvailable);
      values.spaceAddOns = values?.addOns.map((i) => {
        return {
          addOn: {
            id: i.id,
          },
          charge: {
            price: {
              amount: i.charge.price,
              currency: values.spaceCurrency,
              currencySymbol: values.spaceCurrency,
            },
          },
          description: i.description,
        };
      });
      values.chargings.map((nPeriod) => (nPeriod.noticePeriod = values.noticePeriod));
      delete values.meetOrTrain;
      delete values.addOns;
      delete values.maximumSeats;
    } else if (category === 'Shoot') {
      values.keepInMindMeet = values?.keepInMindMeet
        .filter((i) => i && i.isDisabled)
        .map((k) => {
          if (k?.rules) return { rule: k.rules.id, isDisabled: !k.isDisabled };
          else return { rule: k.rule.id, isDisabled: !k.isDisabled };
        });
      values.keepInMindMeet.map((k) => {
        keepInMindTest.map((i) => {
          if (i.rule === k.rule) {
            i.isDisabled = true;
          }
        });
      });
      values.keepInMindMeet = keepInMindTest;
      values.keepInMind = values.keepInMindMeet;
      delete values.keepInMindMeet;

      values.seatArrangements = [{ min: 1, max: parseInt(values.maximumSeats) }];
      values.chargings.map((k) => {
        return Object.assign(k, { pricingOptions: getMeetOrTrainPricingOptions(k.pricingOptions) });
      });
      values.chargings.map((k) => {
        k.pricingOptions = k.pricingOptions.filter((pricingOpt) => {
          if (pricingOpt) return pricingOpt;
        });
        if (k.discounts?.length > 0) {
          k.discounts = k.discounts.filter((dsc) => {
            if (dsc.id) return dsc;
          });
          if (k.discounts.length < 1) delete k.discounts;
        }
      });
      if (meetTrainActivityIds?.length > 0)
        values.eventTypes = meetTrainActivityIds.map((mt) => {
          return { id: mt };
        });
      values.amenities = [];
      values.addOns = (values?.addOns || [])?.filter((i) => i && i.isAvailable);
      values.spaceAddOns = values?.addOns.map((i) => {
        return {
          addOn: {
            id: i.id,
          },
          charge: {
            price: {
              amount: i.charge.price,
              currency: values.spaceCurrency,
              currencySymbol: values.spaceCurrency,
            },
          },
          description: i.description,
        };
      });
      values.chargings.map((nPeriod) => (nPeriod.noticePeriod = values.noticePeriod));
      delete values.meetOrTrain;
      delete values.addOns;
      delete values.maximumSeats;
    }

    if (!values.notTaxRegistered) {
      values.applicableTaxes = values.applicableTaxes.map((i) => {
        return { id: i };
      });
    }
    delete values.category;
    delete values.eventId;

    if (seatingArr.meet && !seatingArr.available) {
      return;
    }
    console.log('onFinish:', values);
    if (isEdit) {
      dispatch(
        updateSpaceDetails(
          { ...values, id: spaceDetails.id },
          spaceDetails.version,
          category,
          fetchSpaceDetails,
          setFetchedData,
          setIsSaving,
          () => {
            resetFormData();
            setFormHasChanged(false);
            setInitialDataSet(false);
            setOriginalFormSnapshot(null);
          }
        )
      );
    } else if (category === 'Work') {
      dispatch(submitSpaceDetails(values, resetFormData, setIsSaving));
    } else if (category === 'Meet' || category === 'Shoot') {
      dispatch(submitSpaceDetailsMeet(values, resetFormData, setIsSaving));
    } else if (category === 'Train') {
      dispatch(submitSpaceDetailsTrain(values, resetFormData, setIsSaving));
    }
  };

  const handleCategoryChange = (category) => {
    form.setFieldsValue({ ...form.getFieldValue(), events: [], evenType: {}, eventId: [] });
    const { venueType, name } = spaceActivities.find((i) => i.id === category);
    setVenueTypes(venueType);
    setCategory(name);
    setMeetTrainActivityIds([]);
    form.setFieldsValue({
      eventType: {
        spaceCategory: {
          id: undefined,
        },
      },
    });
  };

  const handleActivityChange = (category, activityId) => {
    if (category === 'Work') {
      setWorkActivityId(activityId);
    } else {
      setMeetTrainActivityIds(activityId);
    }
  };

  const getVenueName = (activityId) => {
    if (activityId && venueTypes.length > 0) {
      return venueTypes.find((i) => i.id === activityId).code;
    }
  };

  const onFinishFailed = (errorInfo) => {
    delete errorInfo.values.category;

    console.log('Failed:', errorInfo);
    toastr.error('Validation Error');
  };

  console.log('Failed:', workActivityId);

  // console.log("formData->", form.getFieldsValue());

  return (
    <>
      {isEdit && !fetchedData ? (
        <>
          <br />
          <br />
          <br />
          <br />
          <Spin size="large" tip="Loading..."></Spin>
        </>
      ) : (
        <Form
          {...layout}
          form={form}
          name="login"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          onFieldsChange={onFieldsChange}
          initialValues={initialState}
        >
          <Host />
          <BasicDetails
            handleCategoryChange={handleCategoryChange}
            form={form}
            handleActivityChange={handleActivityChange}
            handleCurrencyChange={setCurrency}
            // setSpaceImages={setSpaceImages}
            setSpaceImages={handleImageChange}
            spaceImages={spaceImages}
            category={category}
            setCategory={setCategory}
            setVenueTypes={setVenueTypes}
            venueTypes={venueTypes}
            isEdit={isEdit}
            countryFiltering={countryFiltering}
            countryCodeSelection={countryFiltering}
            showDescriptionError={showDescriptionError}
          />
          {category === 'Work' &&
          workActivityId &&
          getVenueName(workActivityId) === SPACE_ACTIVITIES.HOT_DESK ? (
            <HotDesk
              form={form}
              index={0}
              activityId={workActivityId}
              name={getVenueName(workActivityId)}
              currency={currency}
              isEdit={isEdit}
            />
          ) : (
            <> </>
          )}
          {category === 'Work' &&
            workActivityId &&
            getVenueName(workActivityId) === 'Dedicated Desk' && (
              <DedicatedDesk
                form={form}
                index={0}
                activityId={workActivityId}
                name={getVenueName(workActivityId)}
                currency={currency}
                activity={getVenueName(workActivityId)}
                isEdit={isEdit}
              />
            )}

          {category === 'Work' &&
            workActivityId &&
            (getVenueName(workActivityId) === 'WORK_PRIV' ||
              getVenueName(workActivityId) === 'Booth' || 
              getVenueName(workActivityId) === 'WORK_VIR') && (
              <PrivateOfficeOrBooth
                form={form}
                index={0}
                activityId={workActivityId}
                name={getVenueName(workActivityId)}
                currency={currency}
                activity={getVenueName(workActivityId)}
                isEdit={isEdit}
              />
            )}
          {(category === 'Shoot' || category === 'Meet') && (
            <MeetAndTrain form={form} currency={currency} category={category} isEdit={isEdit} />
          )}
          <Payout countryCodeSelection={countryCodeSelection} isEdit={isEdit} />
          <div className="btn-wrapper">
            <Button
              className="submit-btn"
              type="primary"
              htmlType="submit"
              disabled={descriptionError || (isEdit && !formHasChanged)}
              loading={isSaving}
            >
              {isEdit ? 'Update' : 'Submit'}
            </Button>
          </div>
        </Form>
      )}
    </>
  );
};

export default SpaceEdit;
